// «Артефакты» tab of the «Герои / Артефакты» page: every artefact and relic as a card, grouped by kind, with
// a detail panel on the right like the heroes screen's. The effect text is shop.ts's own lineDesc/relicDesc,
// so the guide, the shop and the skill-bar tooltip can never tell different stories.
import { ARTIFACTS } from '../core/artifacts.js';
import { RELICS, RELIC_LINES } from '../core/relics.js';
import type { ArtifactLine, RelicLine } from '../core/commands.js';
import { ARTIFACT_LINES, ARTIFACT_COST, SELL_REFUND, RELIC_COST, iconImg, lineDesc, relicDesc } from './shop.js';
import { escapeHtml } from './nameStore.js';
import { t } from './i18n/index.js';
import { artifactName, relicName } from './i18n/names.js';

export type GuideGroup = 'passive' | 'active' | 'relic';
export type GuideEntry = {
  id: ArtifactLine | RelicLine; group: GuideGroup; name: string; type: string;
  price: number; cd: string | null; desc: string; notes: string[];
};

export const guideGroups = (): [GuideGroup, string][] => [['passive', t('guide.passive')], ['active', t('guide.active')], ['relic', t('guide.relics')]];

function artifactEntry(line: ArtifactLine): GuideEntry {
  const def = ARTIFACTS[line] as { kind: 'passive' | 'active'; cd?: number };
  const active = def.kind === 'active';
  // Склянка крови has no cooldown to wait out: its "cd" is the time to fill up.
  const cd = !active ? null : line === 'bloodvial' ? t('guide.fullCharge', { cd: def.cd! }) : t('common.cooldown', { cd: def.cd! });
  return {
    id: line, group: def.kind, name: artifactName(line),
    type: active ? t('guide.typeActive') : t('guide.typePassive'),
    price: ARTIFACT_COST, cd, desc: lineDesc(line),
    notes: [
      t('guide.noteBuy', { cost: ARTIFACT_COST }),
      active ? t('guide.noteActive') : t('guide.notePassive'),
      t('guide.noteSell', { n: SELL_REFUND }),
    ],
  };
}

function relicEntry(line: RelicLine): GuideEntry {
  const def = RELICS[line] as { kind: 'passive' | 'active'; cd?: number };
  const active = def.kind === 'active';
  return {
    id: line, group: 'relic', name: relicName(line),
    // A passive relic has nothing to press: T would be a lie on its card.
    type: t('guide.typeRelic', { key: active ? 'T' : t('shop.relic.auto') }),
    price: RELIC_COST, cd: active ? t('tip.sharedCooldown', { cd: def.cd! }) : null, desc: relicDesc(line),
    notes: [t('guide.relicNote1'), t('guide.relicNote2', { cost: RELIC_COST }), t('guide.relicNote3'), t('guide.relicNote4')],
  };
}

/** All twenty cards in display order: passives, actives, relics — each group in core's own table order. */
export function guideEntries(): GuideEntry[] {
  return [...ARTIFACT_LINES.map(artifactEntry), ...RELIC_LINES.map(relicEntry)];
}

function cardHtml(e: GuideEntry): string {
  return `<button type="button" class="ag-card${e.group === 'relic' ? ' relic' : ''}" data-id="${e.id}">
    ${iconImg(e.id, 'ag-ico', 64)}
    <span class="ag-text">
      <span class="ag-name">${escapeHtml(e.name)}</span>
      <span class="ag-type">${e.type}</span>
      <span class="ag-meta"><span class="ag-price">${t('guide.price', { n: e.price })}</span>${e.cd ? `<span class="ag-cd">${e.cd}</span>` : ''}</span>
      <span class="ag-desc">${escapeHtml(e.desc)}</span>
    </span>
  </button>`;
}

export function guideGridHtml(entries: GuideEntry[] = guideEntries()): string {
  return guideGroups().map(([g, label]) => `
    <section class="ag-group" data-group="${g}">
      <h2>${label}</h2>
      <div class="ag-grid">${entries.filter(e => e.group === g).map(cardHtml).join('')}</div>
    </section>`).join('');
}

export function guideDetailHtml(e: GuideEntry): string {
  return `
    <div class="ag-detail-head">
      ${iconImg(e.id, 'ag-detail-ico', 160)}
      <h2>${escapeHtml(e.name)}</h2>
      <p class="hs-role">${e.type}</p>
    </div>
    <div class="hs-stats">
      <div class="hs-stat"><span>${t('guide.cost')}</span><b>${e.price}</b></div>
      ${e.group === 'relic' ? '' : `<div class="hs-stat"><span>${t('guide.sale')}</span><b>+${SELL_REFUND}</b></div>`}
    </div>
    ${e.cd ? `<p class="ag-detail-cd">${e.cd}</p>` : ''}
    <p class="ag-detail-desc">${escapeHtml(e.desc)}</p>
    <ul class="ag-notes">${e.notes.map(n => `<li>${n}</li>`).join('')}</ul>`;
}

export type GuideHandle = { destroy(): void };

export function renderArtifactsGuide(root: HTMLElement): GuideHandle {
  const entries = guideEntries();
  root.innerHTML = `<div class="ag-body"><div class="ag-groups">${guideGridHtml(entries)}</div><div class="hs-detail ag-detail" id="agDetail" aria-live="polite"></div></div>`;
  const detail = root.querySelector('#agDetail') as HTMLElement;
  const cards = [...root.querySelectorAll<HTMLButtonElement>('.ag-card')];
  let selected = '';
  function select(id: string) {
    if (id === selected) return;
    selected = id;
    for (const c of cards) c.classList.toggle('on', c.dataset.id === id);
    detail.innerHTML = guideDetailHtml(entries.find(e => e.id === id)!);
  }
  // focus, not only click: Tab through the cards and the panel follows, like the heroes screen
  for (const c of cards) {
    c.addEventListener('click', () => select(c.dataset.id!));
    c.addEventListener('focus', () => select(c.dataset.id!));
  }
  select(entries[0]!.id);
  return { destroy() { root.innerHTML = ''; } };
}
